import { useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server.browser';
import { Box, Card, CardBody, Heading, Stack, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { BrandProvider, type EmailBrand } from '@duke-hq/email-templates/brand';
import { WelcomeEmail } from '@duke-hq/email-templates/emails/welcome';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

/**
 * What @react-email/render prepends on the way out. Without it the iframe renders
 * in quirks mode, so table and margin behaviour drifts from the delivered email.
 */
const DOCTYPE =
  '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">';

const SAMPLE_PROPS = {
  firstname: 'Alex',
  consultant: 'Sam',
  magicLink: 'https://example.com/magiclink?token=preview',
};

/**
 * The real template, rendered from the values in the form using the same
 * components the sender uses.
 *
 * Rendered to markup rather than mounted: the template's root is <Html>, which
 * cannot nest inside the admin document. The iframe also contains its styles.
 */
export function EmailPreview({ brand }: { brand: EmailBrand }): React.ReactElement {
  const html = useMemo(
    () =>
      DOCTYPE +
      renderToStaticMarkup(
        <BrandProvider brand={brand}>
          <WelcomeEmail {...SAMPLE_PROPS} />
        </BrandProvider>,
      ),
    [brand],
  );

  return (
    <Card borderWidth="1px" borderColor="border.default" shadow="none" height="fit-content">
      <CardBody>
        <Stack gap={3}>
          <Stack gap={1}>
            <Heading fontSize="lg" size="xs">
              <Trans>Preview</Trans>
            </Heading>
            <Text fontSize="sm" color="fg.muted">
              <Trans>
                A real email rendered from what is in the form. Body wording is a sample.
              </Trans>
            </Text>
          </Stack>
          <Box
            as="iframe"
            title="Email preview"
            // A srcDoc iframe is same-origin with the admin app, so anything the
            // preview contains would otherwise run with the admin's session.
            sandbox=""
            srcDoc={html}
            width="100%"
            height={pxToRem(760)}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            bg="white"
          />
        </Stack>
      </CardBody>
    </Card>
  );
}
